import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BackButton from '../components/BackButton';

// Design tokens — "Devtool ML Case Study" (code-editor / file-explorer look)
const PAGE_BG = '#FDF4E3';
const CARD_BG = '#FFFDF7';
const TOOLBAR_BG = '#F8EFDC';
const SIDEBAR_BG = '#FBF3E1';
const INK = '#17140F';
const INK_60 = 'rgba(23,20,15,.6)';
const INK_50 = 'rgba(23,20,15,.5)';
const INK_42 = 'rgba(23,20,15,.42)';
const INK_10 = 'rgba(23,20,15,.10)';
const INK_14 = 'rgba(23,20,15,.14)';
const LINE_NUM = '#CDBEA2';
const PURPLE = '#7A3AAE';
const MAGENTA = '#C0399A';
const BLUE = '#1B65C4';
const ORANGE = '#A85E12';
const GREEN = '#5FB964';
const RED = '#E0503F';
const YELLOW = '#E5A93B';

const FONT_SANS = "'Archivo', system-ui, sans-serif";
const FONT_DISPLAY = "'Archivo Black', 'Archivo', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const TABS = [
  {
    id: 'api',
    file: 'api-development.md',
    eyebrow: '01 · AUTOMOTIVE MIDDLEWARE',
    title: 'API Development',
    description:
      'Replacing a legacy telematics service provider with in-house middleware — and every app-to-vehicle API with it.',
    raw:
      'I led a 30-person cross-functional team to replace an automotive industry legacy TSP (telematics service provider) with our in-house middleware — an industry-defying shift that required rebuilding every app-to-vehicle API. I drove the migration off the monolith, aligned new vehicle hardware to app software specs with developers, guided PMs across dependencies, and partnered with on-site QA to validate vehicle behavior. The result: a faster, cleaner architecture that boosted performance by 25% and cut timelines from months to weeks.',
    highlights: [
      { text: '30-person cross-functional team', color: PURPLE },
      { text: 'app-to-vehicle API', color: BLUE },
      { text: '25%', color: ORANGE },
    ],
    meta: [
      { label: 'ROLE', value: 'Product lead, vehicle platform' },
      { label: 'TEAM', value: '30 · eng, QA, hardware, PM' },
      { label: 'SURFACE', value: 'Middleware, app-to-vehicle APIs' },
    ],
    outcomes: [
      { value: '+25%', color: PURPLE, label: 'end-to-end performance' },
      { value: '30', color: BLUE, label: 'cross-functional team' },
    ],
    status: '+25% performance · months → weeks',
  },
  {
    id: 'ml',
    file: 'ml-platform.md',
    eyebrow: '02 · SELF-SERVE DEPLOYMENT',
    title: 'ML Platform',
    description: "Giving data scientists a path to production that doesn't route through DevOps.",
    raw:
      'Whenever data scientists wanted to deploy their pipelines to production, they needed DevOps and developers to convert their code into a production-ready state. To make a seamless development-to-deployment process, we built a feature so DevOps can create production environments inside the platform. This cut wait times and hurdles, which led to upsells by 3×.',
    highlights: [
      { text: 'data scientists', color: PURPLE },
      { text: 'pipelines to production', color: BLUE },
      { text: 'upsells by 3×', color: ORANGE },
    ],
    meta: [
      { label: 'ROLE', value: 'PM, ML platform' },
      { label: 'USERS', value: 'Data scientists, DevOps' },
      { label: 'OUTCOME', value: '3× upsells, shorter wait times' },
    ],
    outcomes: [
      { value: '3×', color: PURPLE, label: 'platform upsells' },
      { value: 'Self-serve', color: BLUE, label: 'deploys, no dev hand-off' },
    ],
    status: '3× upsells · self-serve environments',
  },
  {
    id: 'integrations',
    file: 'integrations.md',
    eyebrow: '03 · PARTNER SURFACE',
    title: 'Integrations',
    description: 'Connecting the platform to the tools data scientists already live in.',
    raw:
      "For the ML platform, we built 8+ third-party integrations by working with developers, managing vendor relationships, and running user interviews to cover data scientists' end-to-end workflow. Integrations shipped include Databricks, MLflow, GitHub, Ray, Dask, Streamlit, VS Code, and NVIDIA RAPIDS.",
    highlights: [
      { text: '8+ third-party integrations', color: PURPLE },
      { text: 'Databricks, MLflow, GitHub, Ray, Dask, Streamlit, VS Code, and NVIDIA RAPIDS', color: BLUE },
    ],
    meta: [
      { label: 'ROLE', value: 'PM, ML platform' },
      { label: 'INTEGRATIONS', value: 'Databricks, MLflow, GitHub, Ray, Dask, Streamlit, VS Code, RAPIDS' },
      { label: 'OUTCOME', value: 'Full data-science workflow coverage' },
    ],
    outcomes: [
      { value: '8+', color: PURPLE, label: 'integrations shipped' },
      { value: 'Full', color: BLUE, label: 'data-science workflow coverage' },
    ],
    status: '8 integrations shipped',
  },
  {
    id: 'ide',
    file: 'custom-ide-support.md',
    eyebrow: '04 · DEVELOPER ENVIRONMENTS',
    title: 'Custom IDE Support',
    description: "Letting data scientists bring their own editor instead of forcing Jupyter on everyone.",
    raw:
      'The ML platform supported Jupyter notebooks, but not every data scientist wanted to work in one. We extended support to VS Code — and any IDE — by letting users register their machine and SSH directly into their development environment. This lifted user satisfaction and helped retain and upsell customers.',
    highlights: [
      { text: 'any IDE', color: PURPLE },
      { text: 'SSH directly into their development environment', color: BLUE },
      { text: 'retain and upsell customers', color: ORANGE },
    ],
    meta: [
      { label: 'ROLE', value: 'PM, ML platform' },
      { label: 'SURFACE', value: 'SSH-based remote dev, any IDE' },
      { label: 'OUTCOME', value: 'Higher satisfaction, retention & upsell' },
    ],
    outcomes: [
      { value: 'Any IDE', color: PURPLE, label: 'via direct SSH access' },
      { value: 'Higher', color: BLUE, label: 'retention & upsell' },
    ],
    status: 'any IDE via SSH',
  },
  {
    id: 'git',
    file: 'git-status-indicator.md',
    eyebrow: '05 · PIPELINE HEALTH',
    title: 'Git Status Indicator',
    description: 'Surfacing broken syncs before they turn into a broken pipeline.',
    raw:
      "Data scientists didn't realize that pushing bad code meant it silently failed to sync into the platform. Developers kept building on broken pipelines without knowing why. We shipped a status bar and notification system that flags unsynced repos immediately, so bad commits get caught before they cost anyone time. This saved 10% of customers' and developers' time.",
    highlights: [
      { text: 'silently failed to sync', color: PURPLE },
      { text: 'status bar and notification system', color: BLUE },
      { text: '10%', color: ORANGE },
    ],
    meta: [
      { label: 'ROLE', value: 'PM, ML platform' },
      { label: 'USERS', value: 'Data scientists, developers' },
      { label: 'OUTCOME', value: '10% less time lost to bad syncs' },
    ],
    outcomes: [
      { value: '10%', color: PURPLE, label: 'less time lost to bad syncs' },
      { value: 'Faster', color: BLUE, label: 'troubleshooting, fewer broken pipelines' },
    ],
    status: '10% time saved',
  },
];

// Measures the code column's actual rendered width (via a ref on its container)
// and converts it to a character budget for the monospace font, so line wraps
// never overflow the column regardless of viewport, sidebar, or padding changes.
const CODE_COLUMN_OVERHEAD_PX = 52; // line-number column (30px) + grid gap (22px)

function useCodeMaxChars() {
  const containerRef = useRef(null);
  const [maxChars, setMaxChars] = useState(60);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `400 14px ${FONT_MONO}`;
    const charWidth = ctx.measureText('0').width || 8.4;

    const compute = () => {
      const available = el.clientWidth - CODE_COLUMN_OVERHEAD_PX;
      if (available > 0) {
        setMaxChars(Math.max(14, Math.floor(available / charWidth)));
      }
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [containerRef, maxChars];
}

function wrapTextToLines(rawText, maxChars) {
  const words = rawText.split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const tentative = current.length ? current + ' ' + word : word;
    if (tentative.length > maxChars) {
      if (current.length) lines.push(current);
      current = word;
    } else {
      current = tentative;
    }
  });
  if (current.length) lines.push(current);
  return lines;
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Wrap raw text to responsive lines and re-apply colored highlight spans on top.
function useHighlightedLines(raw, highlights, maxChars) {
  return useMemo(() => {
    const lines = wrapTextToLines(raw, maxChars);
    return lines.map((line, i) => {
      let html = line;
      highlights.forEach(({ text, color }) => {
        const re = new RegExp(`(${escapeRegExp(text)})`, 'gi');
        html = html.replace(re, `<span style="color:${color}">$1</span>`);
      });
      return { key: i, html };
    });
  }, [raw, highlights, maxChars]);
}

const CodeBlock = ({ lines }) => (
  <div
    className="grid"
    style={{
      gridTemplateColumns: '30px 1fr',
      columnGap: '22px',
      padding: '22px 0',
      borderTop: `1px solid ${INK_10}`,
      borderBottom: `1px solid ${INK_10}`,
    }}
  >
    {lines.map((line) => (
      <React.Fragment key={line.key}>
        <span
          style={{
            font: `400 14px/2.05 ${FONT_MONO}`,
            color: LINE_NUM,
            textAlign: 'right',
            userSelect: 'none',
          }}
        >
          {line.key + 1}
        </span>
        <span
          style={{ font: `400 14px/2.05 ${FONT_MONO}`, color: INK, whiteSpace: 'pre-wrap' }}
          dangerouslySetInnerHTML={{ __html: line.html }}
        />
      </React.Fragment>
    ))}
  </div>
);

const MetaRow = ({ items }) => (
  <div className="flex flex-wrap" style={{ gap: '26px', marginTop: '26px' }}>
    {items.map((item) => (
      <div key={item.label} className="flex flex-col" style={{ gap: '7px', flex: '1 1 160px' }}>
        <div style={{ font: `700 9.5px/1 ${FONT_MONO}`, letterSpacing: '.14em', color: INK_42 }}>
          {item.label}
        </div>
        <div style={{ font: `500 13.5px/1.45 ${FONT_SANS}`, color: INK }}>{item.value}</div>
      </div>
    ))}
  </div>
);

const ProductDevtoolMLPage = () => {
  const [activeTab, setActiveTab] = useState('ml');

  const [codeColumnRef, maxChars] = useCodeMaxChars();

  const tab = TABS.find((t) => t.id === activeTab) || TABS[0];
  const lines = useHighlightedLines(tab.raw, tab.highlights, maxChars);

  return (
    <motion.div
      className="min-h-screen w-full"
      style={{ backgroundColor: PAGE_BG, fontFamily: FONT_SANS }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <style>{`
        .dtml-nav:hover { background: rgba(23,20,15,.05); }
      `}</style>

      <section className="p-4 md:p-12">
        <div className="w-full mx-auto" style={{ maxWidth: '1280px', marginBottom: '16px' }}>
          <BackButton color={INK_60} hoverColor={INK} fontFamily={FONT_MONO} />
        </div>
        <div
          className="w-full mx-auto"
          style={{
            maxWidth: '1280px',
            background: CARD_BG,
            border: `1px solid ${INK_14}`,
            borderRadius: '12px',
            boxShadow: '0 1px 2px rgba(23,20,15,.04), 0 12px 32px -12px rgba(23,20,15,.16)',
            overflow: 'hidden',
          }}
        >
          {/* Title bar */}
          <div
            className="flex items-center gap-3.5"
            style={{ height: '44px', padding: '0 16px', background: TOOLBAR_BG, borderBottom: `1px solid ${INK_10}` }}
          >
            <div className="flex" style={{ gap: '7px' }}>
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: RED }} />
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: YELLOW }} />
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: GREEN }} />
            </div>
            <span
              className="flex-1 text-center truncate"
              style={{ font: `500 12px/1 ${FONT_MONO}`, color: INK_50, letterSpacing: '.02em' }}
            >
              aki kim · product management / dev tools
            </span>
            {/* Spacer balancing the traffic-light dots so the filename stays centered */}
            <div style={{ width: 47 }} aria-hidden="true" />
          </div>

          <div className="flex flex-col md:flex-row" style={{ minHeight: '640px' }}>
            {/* Sidebar */}
            <div
              className="w-full md:w-[262px] md:flex-none flex flex-col"
              style={{ background: SIDEBAR_BG, borderRight: `1px solid ${INK_10}` }}
            >
              <div style={{ padding: '18px 18px 10px', font: `700 9.5px/1 ${FONT_MONO}`, letterSpacing: '.14em', color: INK_42 }}>
                EXPLORER
              </div>
              <div className="flex flex-col" style={{ padding: '0 10px 16px', gap: '2px' }}>
                <div
                  className="flex items-center"
                  style={{ gap: 7, padding: '6px 9px', font: `500 12px/1 ${FONT_MONO}`, color: INK_60 }}
                >
                  <span style={{ fontSize: 9 }}>▾</span>case-studies
                </div>
                {TABS.map((t) => {
                  const active = t.id === activeTab;
                  return (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveTab(t.id)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveTab(t.id)}
                      className="dtml-nav"
                      style={{
                        padding: '6px 9px 6px 26px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        font: `500 12px/1.5 ${FONT_MONO}`,
                        color: active ? INK : 'rgba(23,20,15,.62)',
                        background: active ? 'rgba(122,58,174,.10)' : 'transparent',
                        boxShadow: active ? `inset 2px 0 0 ${PURPLE}` : 'none',
                      }}
                    >
                      {t.file}
                    </div>
                  );
                })}
              </div>
              <div
                className="flex flex-col mt-auto"
                style={{ padding: '18px', borderTop: `1px solid ${INK_10}`, gap: '16px' }}
              >
                <div style={{ font: `700 9.5px/1 ${FONT_MONO}`, letterSpacing: '.14em', color: INK_42 }}>
                  OUTCOMES
                </div>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={tab.id}
                    className="flex flex-col"
                    style={{ gap: '14px' }}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.18, ease: 'easeInOut' }}
                  >
                    {tab.outcomes.map((o) => (
                      <div key={o.label}>
                        <div
                          style={{
                            font: `400 ${o.value.length > 8 ? 22 : o.value.length > 5 ? 26 : 30}px/1.1 ${FONT_DISPLAY}`,
                            color: o.color,
                            letterSpacing: '-.02em',
                          }}
                        >
                          {o.value}
                        </div>
                        <div style={{ marginTop: 5, font: `400 11.5px/1.4 ${FONT_SANS}`, color: INK_60 }}>
                          {o.label}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Main pane */}
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex overflow-x-auto" style={{ background: TOOLBAR_BG, borderBottom: `1px solid ${INK_10}` }}>
                {TABS.map((t) => {
                  const active = t.id === activeTab;
                  return (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setActiveTab(t.id)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveTab(t.id)}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        font: `500 12px/1 ${FONT_MONO}`,
                        color: active ? INK : INK_50,
                        background: active ? CARD_BG : 'transparent',
                        borderRight: `1px solid ${INK_10}`,
                        boxShadow: active ? `inset 0 -1px 0 ${CARD_BG}, inset 0 2px 0 ${MAGENTA}` : 'none',
                      }}
                    >
                      {t.file}
                    </div>
                  );
                })}
              </div>

              <div className="flex-1 min-w-0 overflow-x-auto px-4 pt-7 pb-6 md:px-11 md:pt-9 md:pb-8">
                <div ref={codeColumnRef}>
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={tab.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: 'easeInOut' }}
                    >
                      <div style={{ font: `500 11px/1 ${FONT_MONO}`, letterSpacing: '.1em', color: INK_42 }}>
                        {tab.eyebrow}
                      </div>
                      <h1
                        style={{
                          margin: '12px 0 4px',
                          font: `400 clamp(30px, 6vw, 46px)/1.04 ${FONT_DISPLAY}`,
                          letterSpacing: '-.025em',
                          color: INK,
                          textWrap: 'pretty',
                        }}
                      >
                        {tab.title}
                      </h1>
                      <p style={{ margin: '0 0 28px', maxWidth: '62ch', font: `400 15px/1.55 ${FONT_SANS}`, color: INK_60 }}>
                        {tab.description}
                      </p>

                      <CodeBlock lines={lines} />
                      <MetaRow items={tab.meta} />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Status bar */}
              <div
                className="flex items-center"
                style={{
                  gap: 18,
                  height: 34,
                  padding: '0 16px',
                  background: TOOLBAR_BG,
                  borderTop: `1px solid ${INK_10}`,
                  font: `500 11px/1 ${FONT_MONO}`,
                  color: INK_50,
                }}
              >
                <span className="flex items-center" style={{ gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: GREEN }} />
                  shipped
                </span>
                <span className="truncate">{tab.status}</span>
                <span className="ml-auto flex-none">UTF-8 · md</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ProductDevtoolMLPage;
